import { Routes } from '@angular/router';

import { authGuard } from './features/authentication/guards/auth.guard';
import { permissionGuard } from './features/authentication/guards/permission.guard';
import { roleGuard } from './features/authentication/guards/role.guard';
import { AppLayoutComponent } from './layout/app-layout/app-layout.component';

export const routes: Routes = [
  // Auth routes (no shell)
  {
    path: 'auth',
    loadChildren: () => import('./features/authentication/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  // Protected routes (with shell)
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard, roleGuard(['Admin', 'SuperAdmin'])],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
        canActivate: [permissionGuard(['DASHBOARD_VIEW'])],
        data: { breadcrumb: 'breadcrumb.dashboard' },
      },
      {
        path: 'establishments',
        loadComponent: () =>
          import('./pages/establishments/establishments.component').then(
            (m) => m.EstablishmentsComponent,
          ),
        canActivate: [permissionGuard(['ESTABLISHMENTS_VIEW_ALL'])],
        data: { breadcrumb: 'breadcrumb.establishments' },
      },
      {
        path: 'establishments/:id',
        loadComponent: () =>
          import('./pages/establishment-detail/establishment-detail.component').then(
            (m) => m.EstablishmentDetailComponent,
          ),
        canActivate: [permissionGuard(['ESTABLISHMENTS_VIEW_ALL'])],
        data: {
          breadcrumb: 'breadcrumb.establishmentDetail',
          breadcrumbParent: { label: 'breadcrumb.establishments', link: '/establishments' },
        },
      },
      {
        path: 'payment-links',
        loadComponent: () =>
          import('./pages/payment-links/payment-links.component').then(
            (m) => m.PaymentLinksComponent,
          ),
        canActivate: [permissionGuard(['PAYMENT_LINKS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.paymentLinks' },
      },
      {
        path: 'payment-links/:id',
        loadComponent: () =>
          import('./pages/payment-link-detail/payment-link-detail.component').then(
            (m) => m.PaymentLinkDetailComponent,
          ),
        canActivate: [permissionGuard(['PAYMENT_LINKS_VIEW'])],
        data: {
          breadcrumb: 'breadcrumb.paymentLinkDetail',
          breadcrumbParent: { label: 'breadcrumb.paymentLinks', link: '/payment-links' },
        },
      },
      {
        path: 'reports',
        loadComponent: () =>
          import('./pages/reports/reports.component').then((m) => m.ReportsComponent),
        canActivate: [permissionGuard(['REPORTS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.reports' },
      },
      {
        path: 'settings/exchange-rates',
        loadComponent: () =>
          import('./pages/settings/exchange-rates/exchange-rates.component').then(
            (m) => m.ExchangeRatesComponent,
          ),
        canActivate: [permissionGuard(['EXCHANGE_RATES_VIEW'])],
        data: { breadcrumb: 'breadcrumb.exchangeRates', breadcrumbParent: null },
      },
      {
        path: 'settings/card-fees',
        redirectTo: '/settings',
        pathMatch: 'full',
      },
      // POS Terminals routes
      {
        path: 'pos-terminals',
        loadComponent: () =>
          import('./pages/settings/pos-terminals/pos-terminals.component').then(
            (m) => m.PosTerminalsComponent,
          ),
        canActivate: [permissionGuard(['POS_TERMINALS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.posTerminals' },
      },
      {
        path: 'pos-terminals/:id',
        loadComponent: () =>
          import('./pages/settings/pos-terminals/pos-terminal-detail/pos-terminal-detail.component').then(
            (m) => m.PosTerminalDetailComponent,
          ),
        canActivate: [permissionGuard(['POS_TERMINALS_VIEW'])],
        data: {
          breadcrumb: 'breadcrumb.posTerminalDetail',
          breadcrumbParent: { label: 'breadcrumb.posTerminals', link: '/pos-terminals' },
        },
      },
      {
        path: 'settings/system-logs',
        loadComponent: () =>
          import('./pages/settings/system-logs/system-logs.component').then(
            (m) => m.SystemLogsComponent,
          ),
        canActivate: [permissionGuard(['SYSTEM_LOGS_VIEW'])],
        data: {
          breadcrumb: 'breadcrumb.systemLogs',
          breadcrumbParent: { label: 'breadcrumb.monitor' },
        },
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings/settings.component').then((m) => m.SettingsComponent),
        canActivate: [permissionGuard(['SETTINGS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.settings' },
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./pages/notifications/notifications.component').then(
            (m) => m.NotificationsComponent,
          ),
        canActivate: [permissionGuard(['NOTIFICATIONS_SEND'])],
        data: { breadcrumb: 'breadcrumb.notifications' },
      },
      {
        path: 'transfer-to-branch',
        loadComponent: () =>
          import('./pages/transfer-to-branch/transfer-to-branch.component').then(
            (m) => m.TransferToBranchComponent,
          ),
        canActivate: [permissionGuard(['TRANSACTIONS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.transferToBranch' },
      },
      {
        path: 'transfer-to-establishment',
        loadComponent: () =>
          import('./pages/transfer-to-establishment/transfer-to-establishment.component').then(
            (m) => m.TransferToEstablishmentComponent,
          ),
        canActivate: [permissionGuard(['TRANSACTIONS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.transferToEstablishment' },
      },
      {
        path: 'transactions',
        loadComponent: () =>
          import('./pages/transactions/transactions.component').then(
            (m) => m.TransactionsComponent,
          ),
        canActivate: [permissionGuard(['TRANSACTIONS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.transactions' },
      },
      {
        path: 'cancellations',
        loadComponent: () =>
          import('./pages/cancellations/cancellations.component').then(
            (m) => m.CancellationsComponent,
          ),
        canActivate: [permissionGuard(['TRANSACTIONS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.cancellations' },
      },
      {
        path: 'chargebacks/:id/contestation',
        loadComponent: () =>
          import('./pages/chargebacks/contestation/contestation.component').then(
            (m) => m.ContestationComponent,
          ),
        canActivate: [roleGuard(['Admin', 'SuperAdmin'])],
        data: {
          breadcrumb: 'breadcrumb.contestation',
          breadcrumbParent: { label: 'breadcrumb.chargebacks', link: '/chargebacks' },
        },
      },
      {
        path: 'chargebacks',
        loadComponent: () =>
          import('./pages/chargebacks/chargebacks.component').then((m) => m.ChargebacksComponent),
        canActivate: [roleGuard(['Admin', 'SuperAdmin'])],
        data: { breadcrumb: 'breadcrumb.chargebacks' },
      },
      {
        path: 'transactions/:id',
        loadComponent: () =>
          import('./pages/transaction-detail/transaction-detail.component').then(
            (m) => m.TransactionDetailComponent,
          ),
        canActivate: [permissionGuard(['TRANSACTIONS_VIEW'])],
        data: {
          breadcrumb: 'breadcrumb.transactionDetail',
          breadcrumbParent: { label: 'breadcrumb.transactions', link: '/transactions' },
        },
      },
      // User management routes
      {
        path: 'users',
        loadComponent: () =>
          import('./pages/users/bulk-actions/bulk-actions.component').then(
            (m) => m.BulkActionsComponent,
          ),
        canActivate: [permissionGuard(['USERS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.users' },
      },
      // Role management route
      {
        path: 'roles',
        loadComponent: () => import('./pages/roles/roles.component').then((m) => m.RolesComponent),
        canActivate: [permissionGuard(['ROLES_VIEW'])],
        data: { breadcrumb: 'breadcrumb.roles' },
      },
      // Permission management route
      {
        path: 'permissions',
        loadComponent: () =>
          import('./pages/permissions/permissions.component').then((m) => m.PermissionsComponent),
        canActivate: [permissionGuard(['PERMISSIONS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.permissions' },
      },
      // Monitor routes
      {
        path: 'monitor/logs/dashboard',
        loadComponent: () =>
          import('./pages/monitor/logs/dashboard/log-dashboard.component').then(
            (m) => m.LogDashboardComponent,
          ),
        canActivate: [permissionGuard(['MONITOR_LOGS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.logDashboard' },
      },
      {
        path: 'monitor/logs',
        loadComponent: () =>
          import('./pages/monitor/logs/logs.component').then((m) => m.LogsComponent),
        canActivate: [permissionGuard(['MONITOR_LOGS_VIEW'])],
        data: { breadcrumb: 'breadcrumb.requestLogs' },
      },
      {
        path: 'monitor/storage',
        loadComponent: () =>
          import('./pages/monitor/storage/storage.component').then((m) => m.StorageComponent),
        canActivate: [permissionGuard(['MONITOR_STORAGE_VIEW'])],
        data: { breadcrumb: 'breadcrumb.storage' },
      },
      {
        path: 'monitor/database',
        loadComponent: () =>
          import('./pages/monitor/database/database.component').then((m) => m.DatabaseComponent),
        canActivate: [permissionGuard(['MONITOR_DATABASE_VIEW'])],
        data: { breadcrumb: 'breadcrumb.database' },
      },
      // Compliance routes
      {
        path: 'compliance/audit-logs',
        loadComponent: () =>
          import('./pages/audit-logs/audit-logs.component').then((m) => m.AuditLogsComponent),
        canActivate: [permissionGuard(['AUDIT_LOGS_VIEW'])],
        data: {
          breadcrumb: 'breadcrumb.auditLogs',
          breadcrumbParent: { label: 'breadcrumb.monitor' },
        },
      },
      {
        path: 'compliance/settlement',
        loadComponent: () =>
          import('./pages/settlement-dashboard/settlement-dashboard.component').then(
            (m) => m.SettlementDashboardComponent,
          ),
        canActivate: [permissionGuard(['SETTLEMENT_VIEW'])],
        data: { breadcrumb: 'breadcrumb.settlement' },
      },
      // Reconciliation routes (new /reconciliation/* prefix)
      {
        path: 'reconciliation/provider',
        loadComponent: () =>
          import('./pages/provider-reconciliation/provider-reconciliation.component').then(
            (m) => m.ProviderReconciliationComponent,
          ),
        canActivate: [roleGuard(['Admin', 'SuperAdmin'])],
        data: {
          breadcrumb: 'breadcrumb.providerReconciliation',
          breadcrumbParent: { label: 'breadcrumb.reconciliation' },
        },
      },
      {
        path: 'reconciliation/dashboard',
        loadComponent: () =>
          import('./pages/provider-reconciliation/components/reconciliation-dashboard.component').then(
            (m) => m.ReconciliationDashboardComponent,
          ),
        canActivate: [roleGuard(['Admin', 'SuperAdmin'])],
        data: {
          breadcrumb: 'breadcrumb.reconciliationDashboard',
          breadcrumbParent: { label: 'breadcrumb.reconciliation' },
        },
      },
      {
        path: 'reconciliation/provider-transactions',
        loadComponent: () =>
          import('./pages/provider-transactions/provider-transactions.component').then(
            (m) => m.ProviderTransactionsComponent,
          ),
        canActivate: [roleGuard(['Admin', 'SuperAdmin'])],
        data: {
          breadcrumb: 'breadcrumb.providerTransactions',
          breadcrumbParent: { label: 'breadcrumb.reconciliation' },
        },
      },
      {
        path: 'commpix-test',
        loadComponent: () =>
          import('./pages/commpix-test/commpix-test.component').then((m) => m.CommPixTestComponent),
        canActivate: [roleGuard(['SuperAdmin'])],
        data: { breadcrumb: 'CommPix Test' },
      },
      // Backward compatibility redirects from old /compliance/* paths
      {
        path: 'compliance/provider-reconciliation',
        redirectTo: '/reconciliation/provider',
        pathMatch: 'full',
      },
      {
        path: 'compliance/provider-reconciliation-dashboard',
        redirectTo: '/reconciliation/dashboard',
        pathMatch: 'full',
      },
      {
        path: 'compliance/provider-transactions',
        redirectTo: '/reconciliation/provider-transactions',
        pathMatch: 'full',
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
